import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";
import { DigestTrend } from "@/components/DigestTrend";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtWeek = (d) => {
  const s = new Date(d.week_start + "T00:00:00"), e = new Date(d.week_end + "T00:00:00");
  const f = (x, o) => x.toLocaleDateString(undefined, o);
  return `${f(s, { month: "short", day: "numeric" })} – ${f(e, { month: "short", day: "numeric", year: "numeric" })}`;
};
const hrs = (h) => (h === null || h === undefined ? "—" : `${h}h`);

const Tile = ({ n, label, cls = "", testid }) => (
  <div className="border border-border rounded-sm p-3" data-testid={testid}>
    <div className={`text-2xl font-condensed font-extrabold ${cls}`}>{n}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

export function TicketDigestDialog({ open, setOpen, digestId, isAdmin }) {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/tickets/digests").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (!open || !list.length) return; setSel((s) => list.find((d) => d.id === (digestId || s?.id)) || list[0]); }, [open, list, digestId]);
  const runNow = async () => {
    setBusy(true);
    try { const r = await api.post("/tickets/digests/run"); toast.success("Digest generated and sent to managers"); await load(); setSel(r.data); }
    catch { toast.error("Could not generate digest"); } finally { setBusy(false); }
  };
  const t = sel?.totals;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="digest-dialog">
        <DialogHeader>
          <DialogTitle>Weekly Monge Ticketing Digest</DialogTitle>
          <DialogDescription>Last week's tickets, SLA breaches and average resolution per support group. Sent to managers every Monday morning.</DialogDescription>
        </DialogHeader>
        {open && <DigestTrend selectedWeek={sel?.week_start} />}
        {list.length ? (
          <div className="space-y-4">
            <Select value={sel?.id || ""} onValueChange={(v) => setSel(list.find((d) => d.id === v))}>
              <SelectTrigger className="w-72" data-testid="digest-week-select"><SelectValue placeholder="Pick a week" /></SelectTrigger>
              <SelectContent>{list.map((d) => <SelectItem key={d.id} value={d.id}>Week of {fmtWeek(d)}</SelectItem>)}</SelectContent>
            </Select>
            {t && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                <Tile n={t.created} label="New" cls="text-primary" testid="digest-created" />
                <Tile n={t.resolved} label="Resolved" cls="text-emerald-600" testid="digest-resolved" />
                <Tile n={t.breached} label="SLA breaches" cls={t.breached ? "text-red-600" : ""} testid="digest-breached" />
                <Tile n={hrs(t.avg_resolution_hours)} label="Avg resolution" testid="digest-avg" />
                <Tile n={t.open_now} label="Still open" testid="digest-open" />
              </div>
            )}
            <div className="border border-border rounded-sm overflow-hidden">
              <table className="w-full text-sm" data-testid="digest-groups-table">
                <thead className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr><th className="text-left px-3 py-2">Group</th><th className="px-3 py-2 text-right">New</th><th className="px-3 py-2 text-right">Resolved</th><th className="px-3 py-2 text-right">Auto-resolved</th><th className="px-3 py-2 text-right">Breaches</th><th className="px-3 py-2 text-right">Avg resolution</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sel?.groups?.length ? sel.groups.map((g) => (
                    <tr key={g.group_name} data-testid={`digest-row-${g.group_name}`}>
                      <td className="px-3 py-2 font-medium">{g.group_name}</td>
                      <td className="px-3 py-2 text-right">{g.created}</td>
                      <td className="px-3 py-2 text-right">{g.resolved}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{g.auto_resolved}</td>
                      <td className={`px-3 py-2 text-right ${g.breached ? "text-red-600 font-semibold" : ""}`}>{g.breached}</td>
                      <td className="px-3 py-2 text-right">{hrs(g.avg_resolution_hours)}</td>
                    </tr>
                  )) : <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground text-xs">No ticket activity that week.</td></tr>}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">Generated {new Date(sel?.generated_at).toLocaleString()}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="digest-empty">No digests yet — the first one arrives next Monday{isAdmin ? ", or generate one now." : "."}</p>
        )}
        {(isAdmin || sel) && (
          <DialogFooter className="gap-2">
            {sel && <Button onClick={() => window.open(`${BACKEND}/api/tickets/digests/${sel.id}/pdf`, "_blank")} data-testid="digest-pdf"><FileDown className="h-4 w-4 mr-1.5" />Download PDF</Button>}
            {isAdmin && <Button variant="outline" onClick={runNow} disabled={busy} data-testid="digest-run-now"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Generate last week now</Button>}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
